// ENT Surgery Guide — procedure content
// Full detailed content for top 5 procedures; concise overviews for the remaining 5.

export const PROCEDURES = [
  {
    slug: "fess",
    name: "Functional Endoscopic Sinus Surgery (FESS)",
    short: "FESS",
    category: "Nose & Sinus",
    tagline:
      "Minimally invasive endoscopic surgery to restore natural sinus drainage and relieve chronic sinusitis.",
    detailed: true,
    overview:
      "Functional Endoscopic Sinus Surgery (FESS) is a minimally invasive procedure used to treat chronic sinusitis, nasal polyps and recurrent sinus infections that do not respond to medication. Using a high-definition endoscope passed through the nostrils, Dr. Sanjay Teza removes blockages, diseased tissue and polyps to reopen the natural sinus drainage pathways, without any external incisions or facial scars.",
    symptoms: [
      "Persistent nasal blockage or congestion",
      "Facial pain, pressure or heaviness",
      "Thick nasal discharge or post-nasal drip",
      "Reduced or lost sense of smell",
      "Recurrent sinus infections or headaches",
    ],
    diagnosis:
      "Diagnosis involves a detailed history, nasal endoscopy to visualise the sinus openings and, where required, a CT scan of the paranasal sinuses to map the exact extent of disease before planning surgery.",
    procedureSteps: [
      "Performed under general anaesthesia as a day-care or short-stay procedure.",
      "A thin endoscope is guided through the nostrils to inspect the sinuses.",
      "Blockages, polyps and diseased tissue are precisely removed with micro-instruments.",
      "Natural drainage channels are widened to restore normal sinus ventilation.",
      "No external cuts are made, preserving facial structure and appearance.",
    ],
    recovery:
      "Most patients go home the same day or the next morning. Mild congestion and light discharge are normal for 1–2 weeks. Saline nasal rinses and prescribed sprays support healing. Most people return to routine work within 5–7 days and experience marked relief from sinus symptoms.",
    faqs: [
      {
        q: "Is FESS a painful surgery?",
        a: "FESS is performed under anaesthesia so you feel nothing during surgery. Afterwards there is only mild discomfort, easily managed with medication.",
      },
      {
        q: "Will there be any scars on my face?",
        a: "No. FESS is done entirely through the nostrils using an endoscope, so there are no external incisions or facial scars.",
      },
      {
        q: "How soon will my sinus symptoms improve?",
        a: "Many patients notice easier breathing within days, with progressive improvement as swelling settles over 2–3 weeks.",
      },
    ],
  },
  {
    slug: "septoplasty",
    name: "Septoplasty (Deviated Nasal Septum Correction)",
    short: "Septoplasty",
    category: "Nose & Sinus",
    tagline:
      "Straightening a deviated nasal septum to restore clear, balanced breathing through both nostrils.",
    detailed: true,
    overview:
      "Septoplasty corrects a deviated nasal septum, the bone and cartilage wall that divides the two nostrils. When the septum is crooked, it obstructs airflow and can cause chronic congestion, snoring and recurrent infections. The surgery straightens and repositions the septum through the nostrils, improving breathing without changing the external shape of the nose.",
    symptoms: [
      "Blocked nose, often worse on one side",
      "Difficulty breathing, especially at night",
      "Snoring or disturbed sleep",
      "Recurrent nosebleeds or sinus infections",
      "Facial pressure and headaches",
    ],
    diagnosis:
      "Assessment includes an examination of the nasal passages and nasal endoscopy to confirm the deviation and identify any associated sinus disease that may need attention during the same surgery.",
    procedureSteps: [
      "Carried out under general or local anaesthesia as a day-care procedure.",
      "An incision is made inside the nostril, leaving no visible external scar.",
      "The deviated portions of cartilage and bone are trimmed and repositioned.",
      "The septum is straightened to open both nasal airways evenly.",
      "Often combined with turbinate reduction or FESS when indicated.",
    ],
    recovery:
      "Patients usually go home the same day. Mild congestion and a blocked feeling are common for the first week as internal swelling settles. Heavy lifting and strenuous activity are avoided for about 2 weeks. Clear, comfortable breathing typically improves steadily over 3–4 weeks.",
    faqs: [
      {
        q: "Will septoplasty change how my nose looks?",
        a: "No. Septoplasty works on the internal septum only. The external shape of your nose stays the same unless a cosmetic procedure is combined by choice.",
      },
      {
        q: "Are nasal packs always required?",
        a: "Modern techniques often avoid heavy packing. If used, soft dissolvable or removable packs are placed briefly for support.",
      },
      {
        q: "Is the improvement in breathing permanent?",
        a: "Yes. Once the septum is straightened and healed, the improvement in airflow is long-lasting.",
      },
    ],
  },
  {
    slug: "tympanoplasty",
    name: "Tympanoplasty (Ear Drum Reconstruction)",
    short: "Tympanoplasty",
    category: "Ear",
    tagline:
      "Microscopic reconstruction of a perforated ear drum to stop discharge and improve hearing.",
    detailed: true,
    overview:
      "Tympanoplasty is a microscopic ear surgery to repair a perforated (torn) ear drum and reconstruct the hearing mechanism. Performed with a Carl Zeiss operating microscope, it closes long-standing perforations caused by chronic ear infections or injury, helping to stop recurrent discharge and restore hearing.",
    symptoms: [
      "Recurrent ear discharge",
      "Hearing loss in the affected ear",
      "A sensation of fullness or blockage",
      "History of a persistent ear-drum perforation",
      "Ringing sound (tinnitus) in the ear",
    ],
    diagnosis:
      "Diagnosis involves otoscopic and microscopic ear examination, a hearing test (audiometry) to assess the degree of hearing loss, and imaging in selected cases to evaluate the middle ear.",
    procedureSteps: [
      "Performed under general or local anaesthesia using an operating microscope.",
      "Accessed through the ear canal or a small incision behind the ear.",
      "The perforation edges are prepared and cleaned of diseased tissue.",
      "A graft (usually the patient's own tissue) is placed to rebuild the ear drum.",
      "The hearing bones are checked and reconstructed if required.",
    ],
    recovery:
      "Most patients are discharged within a day. The ear is kept dry during healing, with follow-up to monitor graft uptake over 4–6 weeks. Hearing improvement is usually appreciated once healing completes. Water precautions and avoiding forceful nose-blowing are advised early on.",
    faqs: [
      {
        q: "Will my hearing fully return after tympanoplasty?",
        a: "Most patients experience meaningful hearing improvement. The final result depends on the ear-drum healing and the condition of the hearing bones.",
      },
      {
        q: "How long must I keep the ear dry?",
        a: "You will be advised to keep water out of the ear for several weeks until the graft has fully healed and the doctor confirms it is safe.",
      },
      {
        q: "Will the ear discharge stop after surgery?",
        a: "Closing the perforation typically stops the recurrent discharge that occurs when the middle ear is exposed to the outside.",
      },
    ],
  },
  {
    slug: "mastoidectomy",
    name: "Mastoidectomy",
    short: "Mastoidectomy",
    category: "Ear",
    tagline:
      "Removal of infected mastoid air cells and cholesteatoma to make the ear safe and dry.",
    detailed: true,
    overview:
      "Mastoidectomy is a surgery to remove infected air cells in the mastoid bone behind the ear, often performed for chronic ear infection with cholesteatoma (an abnormal skin growth in the middle ear). The goal is to create a safe, dry ear, prevent serious complications and, where possible, preserve or reconstruct hearing.",
    symptoms: [
      "Long-standing, foul-smelling ear discharge",
      "Progressive hearing loss",
      "Ear pain or pressure behind the ear",
      "Dizziness or imbalance in advanced cases",
      "Diagnosis of cholesteatoma on examination",
    ],
    diagnosis:
      "Careful microscopic ear examination is combined with audiometry and a CT scan of the temporal bone to map the extent of disease and plan a safe surgical approach.",
    procedureSteps: [
      "Performed under general anaesthesia via an incision behind the ear.",
      "The diseased mastoid air cells and cholesteatoma are meticulously removed.",
      "Vital structures such as the facial nerve are carefully protected.",
      "The ear is cleared of infection to prevent dangerous complications.",
      "Ear-drum and hearing reconstruction are done in the same or a staged sitting.",
    ],
    recovery:
      "A short hospital stay is usual. The ear is kept dry, with a dressing changed as advised. Follow-up over several weeks monitors healing. Depending on the type performed, periodic cleaning of the ear cavity may be advised long-term to keep it healthy.",
    faqs: [
      {
        q: "Why is mastoidectomy sometimes necessary?",
        a: "It is needed when chronic infection or cholesteatoma threatens the ear and surrounding structures. Removing the disease prevents serious complications.",
      },
      {
        q: "Will I need more than one surgery?",
        a: "In some cases hearing reconstruction is staged for the best outcome. Dr. Teza will explain if a second sitting is advisable in your case.",
      },
      {
        q: "Is mastoidectomy a safe procedure?",
        a: "Performed with modern equipment and microscopic precision, it is a well-established, safe surgery that protects vital nearby structures.",
      },
    ],
  },
  {
    slug: "tonsillectomy",
    name: "Tonsillectomy",
    short: "Tonsillectomy",
    category: "Throat",
    tagline:
      "Surgical removal of the tonsils to end recurrent throat infections and obstructed breathing.",
    detailed: true,
    overview:
      "Tonsillectomy is the surgical removal of the tonsils, recommended for repeated throat infections, enlarged tonsils causing breathing or swallowing difficulty, or obstructive sleep apnoea. Modern techniques allow precise removal with reduced bleeding and a smoother recovery.",
    symptoms: [
      "Frequent or recurrent sore throats and tonsillitis",
      "Difficulty swallowing due to enlarged tonsils",
      "Snoring or interrupted breathing during sleep",
      "Persistent bad breath or tonsil stones",
      "Muffled voice from very large tonsils",
    ],
    diagnosis:
      "Diagnosis is based on a throat examination, the frequency and severity of infections, and an assessment of any sleep or breathing difficulty caused by the enlarged tonsils.",
    procedureSteps: [
      "Performed under general anaesthesia as a day-care or short-stay procedure.",
      "The tonsils are removed through the mouth, leaving no external scars.",
      "Precise techniques are used to control bleeding and protect surrounding tissue.",
      "Adenoids may be removed in the same sitting when indicated.",
      "The throat is left to heal naturally without stitches.",
    ],
    recovery:
      "A sore throat lasting about a week is expected and managed with pain relief, cool fluids and a soft diet. Most children and adults return to normal routine within 7–10 days. Adequate hydration and following the diet advice help healing and reduce discomfort.",
    faqs: [
      {
        q: "At what point should tonsils be removed?",
        a: "Surgery is usually advised for repeated infections, breathing difficulty, sleep apnoea, or when tonsils significantly affect quality of life.",
      },
      {
        q: "Is tonsillectomy safe for children?",
        a: "Yes. It is one of the most common and well-established ENT surgeries in children when clearly indicated.",
      },
      {
        q: "What can I eat after the surgery?",
        a: "Cool, soft and soothing foods are ideal in the first days. Spicy, hard or hot foods are avoided until the throat heals.",
      },
    ],
  },
  {
    slug: "adenoidectomy",
    name: "Adenoidectomy",
    short: "Adenoidectomy",
    category: "Throat",
    tagline:
      "Removal of enlarged adenoids to relieve nasal blockage, mouth breathing and recurrent ear infections in children.",
    detailed: true,
    overview:
      "Adenoidectomy removes enlarged adenoid tissue at the back of the nose that commonly causes nasal blockage, mouth breathing, snoring and recurrent ear infections in children. It is often combined with tonsil surgery or ear-tube placement when needed and offers quick, reliable relief and better sleep, hearing and facial development.",
    symptoms: [
      "Chronic nasal blockage and persistent mouth breathing",
      "Snoring or disturbed, restless sleep",
      "Recurrent ear infections or fluid behind the ear drum",
      "Nasal voice and frequent cold-like symptoms",
      "Poor sleep leading to daytime tiredness or irritability in children",
    ],
    diagnosis:
      "Diagnosis involves examining the nose and throat, a nasal endoscopy or an X-ray of the post-nasal space to assess adenoid size, and a hearing assessment when recurrent ear problems are present.",
    procedureSteps: [
      "Performed under general anaesthesia as a short day-care procedure.",
      "The adenoids are removed through the mouth, with no external incisions.",
      "Bleeding is carefully controlled and the area left to heal naturally.",
      "Often combined with tonsillectomy or ear-tube (grommet) insertion when indicated.",
      "The child is monitored briefly and usually discharged the same day.",
    ],
    recovery:
      "Recovery is quick and comfortable. Mild throat discomfort, nasal congestion or a slightly nasal voice may last a few days. Soft foods and plenty of fluids are advised. Most children return to school within 3–5 days and enjoy clearer breathing and better sleep soon after.",
    faqs: [
      {
        q: "Is adenoidectomy only for children?",
        a: "It is most common in children, as adenoids naturally shrink with age, but it is occasionally needed in adults too.",
      },
      {
        q: "How long is recovery?",
        a: "Recovery is usually quick, with most children returning to normal activity within a few days.",
      },
      {
        q: "Will removing the adenoids weaken my child's immunity?",
        a: "No. The adenoids are only a small part of the body's immune system, and other tissues take over their function. Removing diseased adenoids usually means fewer infections overall.",
      },
      {
        q: "Why is it often done along with ear tubes?",
        a: "Enlarged adenoids can block the tube that ventilates the middle ear. Removing them, sometimes with grommet insertion, helps clear fluid and improve hearing.",
      },
    ],
  },
  {
    slug: "thyroid-surgery",
    name: "Thyroid Surgery (Thyroidectomy)",
    short: "Thyroid Surgery",
    category: "Head & Neck",
    tagline:
      "Precise removal of thyroid nodules or the thyroid gland for goitre, nodules and thyroid cancer.",
    detailed: true,
    overview:
      "Thyroid surgery involves removing part or all of the thyroid gland to treat enlarged goitres, suspicious or growing nodules, and thyroid cancer. Dr. Sanjay Teza performs meticulous head and neck surgery with careful protection of the voice nerves and parathyroid glands for a safe outcome and a neat, well-hidden neck scar.",
    symptoms: [
      "A visible swelling or lump in the front of the neck",
      "A growing thyroid nodule or a nodule that appears suspicious on scan",
      "Difficulty swallowing or a feeling of pressure in the throat",
      "Difficulty breathing or a change in voice from a large goitre",
      "Overactive thyroid not controlled by medication",
    ],
    diagnosis:
      "Evaluation includes a neck examination, thyroid function blood tests, an ultrasound of the neck, and a fine-needle aspiration (FNAC) of the nodule when required to determine whether surgery is needed.",
    procedureSteps: [
      "Performed under general anaesthesia through a small, carefully placed neck incision.",
      "Part of the gland (hemithyroidectomy) or the whole gland (total thyroidectomy) is removed as planned.",
      "The recurrent laryngeal (voice) nerves are identified and protected throughout.",
      "The parathyroid glands, which control calcium, are preserved carefully.",
      "The incision is closed with a fine, cosmetically favourable technique.",
    ],
    recovery:
      "Most patients stay in hospital for a day or two. Mild neck discomfort and a tight feeling on swallowing settle within days. Calcium and thyroid hormone levels are monitored, and replacement medication is started if needed. Normal activities usually resume within 1–2 weeks, with the scar fading steadily over months.",
    faqs: [
      {
        q: "Will thyroid surgery affect my voice?",
        a: "The nerves controlling the voice are carefully identified and protected during surgery to preserve normal voice function.",
      },
      {
        q: "Will I need medication after surgery?",
        a: "If a large part of the gland is removed, thyroid hormone replacement may be advised. This will be explained clearly beforehand.",
      },
      {
        q: "Will there be a large scar on my neck?",
        a: "The incision is kept small and placed in a natural skin crease so that, once healed, it is discreet and difficult to notice.",
      },
      {
        q: "Is every thyroid nodule cancerous?",
        a: "No. Most thyroid nodules are benign. Surgery is advised for suspicious, growing or symptomatic nodules, guided by scans and FNAC results.",
      },
    ],
  },
  {
    slug: "microlaryngeal-surgery",
    name: "Microlaryngeal Surgery",
    short: "Microlaryngeal Surgery",
    category: "Throat",
    tagline:
      "Microscopic voice-box surgery to treat vocal cord nodules, polyps and hoarseness.",
    detailed: true,
    overview:
      "Microlaryngeal surgery is a delicate, microscope-assisted procedure on the voice box to treat vocal cord nodules, polyps, cysts and persistent hoarseness. Performed through the mouth without external incisions, it aims to restore a clear, healthy voice while preserving vocal cord function using precise micro-instruments and, where appropriate, laser.",
    symptoms: [
      "Persistent hoarseness or a change in voice lasting more than a few weeks",
      "Voice fatigue or loss of voice, especially in singers and heavy voice users",
      "A breathy, rough or strained voice quality",
      "A sensation of a lump or irritation in the throat",
      "Vocal cord nodules, polyps or cysts seen on examination",
    ],
    diagnosis:
      "Diagnosis is made with a detailed voice history and visualisation of the vocal cords using a laryngeal endoscope or stroboscopy to identify the exact lesion and assess vocal cord movement before planning surgery.",
    procedureSteps: [
      "Performed under general anaesthesia with the surgeon using an operating microscope.",
      "A laryngoscope is passed through the mouth to expose the voice box — no external cuts.",
      "The nodule, polyp or cyst is removed precisely while protecting healthy vocal cord tissue.",
      "Micro-instruments or laser are used depending on the lesion.",
      "The airway and voice mechanism are preserved for the best functional outcome.",
    ],
    recovery:
      "A short period of voice rest is usually advised after surgery to allow the vocal cords to heal. Most patients go home the same day. The voice improves gradually over 1–3 weeks, and voice therapy may be recommended to protect long-term voice health and prevent recurrence.",
    faqs: [
      {
        q: "Who needs microlaryngeal surgery?",
        a: "It is advised for persistent hoarseness or voice change due to vocal cord lesions that do not settle with voice rest and medication.",
      },
      {
        q: "Will my voice return to normal?",
        a: "Most patients regain a clear voice after healing and, where advised, a short period of voice rest and therapy.",
      },
      {
        q: "How long should I rest my voice after surgery?",
        a: "A few days of complete or relative voice rest is typically advised. Dr. Teza will give you a personalised plan based on your surgery and profession.",
      },
      {
        q: "Can the problem come back?",
        a: "Recurrence is uncommon when the underlying voice strain is addressed. Voice therapy and good vocal habits greatly reduce the chance of it returning.",
      },
    ],
  },
  {
    slug: "ear-drum-repair",
    name: "Ear Drum Repair (Myringoplasty)",
    short: "Ear Drum Repair",
    category: "Ear",
    tagline:
      "Focused repair of a perforated ear drum to stop discharge and protect hearing.",
    detailed: true,
    overview:
      "Ear drum repair (myringoplasty) is a focused procedure to close a hole in the ear drum without touching the hearing bones. It stops recurrent discharge, protects the middle ear from infection and helps improve hearing, often performed as a quick, minimally invasive day-care surgery using a graft of the patient's own tissue.",
    symptoms: [
      "A known perforation (hole) in the ear drum",
      "Recurrent or intermittent ear discharge",
      "Mild to moderate hearing loss in the affected ear",
      "Water entering the ear causing repeated infections",
      "A feeling of blockage or occasional ringing in the ear",
    ],
    diagnosis:
      "The ear is examined under a microscope to confirm the size and position of the perforation, and a hearing test (audiometry) is done to measure hearing before deciding on repair.",
    procedureSteps: [
      "Performed under local or general anaesthesia as a day-care procedure.",
      "Accessed through the ear canal or a tiny incision, leaving minimal visible scarring.",
      "The edges of the perforation are freshened to encourage healing.",
      "A thin graft of the patient's own tissue is placed to seal the hole.",
      "The hearing bones are left untouched, as only the ear drum is being repaired.",
    ],
    recovery:
      "Most patients go home the same day. The ear is kept completely dry while the graft heals over 3–6 weeks. Forceful nose-blowing and water exposure are avoided early on. Once healed, the recurrent discharge stops and hearing often improves, with a follow-up to confirm the graft has taken.",
    faqs: [
      {
        q: "How is ear drum repair different from tympanoplasty?",
        a: "Myringoplasty repairs only the ear drum, while tympanoplasty may also address the hearing bones. Dr. Teza will advise which is right for you.",
      },
      {
        q: "Is it a major surgery?",
        a: "It is a relatively minor, well-tolerated procedure, often done as day-care surgery with a quick recovery.",
      },
      {
        q: "How successful is the repair?",
        a: "Ear drum repair has a high success rate. When the ear is dry at the time of surgery and aftercare is followed, the graft usually heals well.",
      },
      {
        q: "Can I swim after the ear drum heals?",
        a: "Yes. Once the doctor confirms complete healing, you can return to swimming and normal water activities safely.",
      },
    ],
  },
  {
    slug: "foreign-body-removal",
    name: "Foreign Body Removal (Ear, Nose & Throat)",
    short: "Foreign Body Removal",
    category: "Emergency ENT",
    tagline:
      "Safe, prompt removal of objects lodged in the ear, nose or throat, common in children.",
    detailed: true,
    overview:
      "Foreign body removal is the safe extraction of objects stuck in the ear, nose or throat, a frequent emergency in children. Using endoscopic and microscopic guidance, Dr. Sanjay Teza removes foreign bodies gently and quickly, minimising injury and distress while preventing complications such as infection, bleeding or airway obstruction.",
    symptoms: [
      "A known or suspected object placed in the ear, nose or throat",
      "Sudden ear or nasal pain, blockage or discharge",
      "Foul-smelling, one-sided nasal discharge (common with a nasal foreign body)",
      "Coughing, choking, drooling or difficulty swallowing",
      "Noisy or difficult breathing in severe throat or airway cases",
    ],
    diagnosis:
      "The ear, nose or throat is examined directly and with an endoscope or microscope to locate the object. In selected cases, an X-ray is used to confirm the position of a hidden or swallowed foreign body.",
    procedureSteps: [
      "Many objects are removed safely in the clinic under direct vision.",
      "Endoscopic or microscopic guidance is used for precise, atraumatic removal.",
      "Brief general anaesthesia may be used for anxious children or deep objects.",
      "Special care is taken with button batteries and sharp objects, which are urgent.",
      "The area is checked afterwards for any injury, swelling or infection.",
    ],
    recovery:
      "Recovery is usually immediate once the object is removed. Mild soreness or slight discharge may occur for a day or two, and a short course of drops or medication may be advised. Any injury caused by the object is monitored to ensure complete healing.",
    faqs: [
      {
        q: "What should I do if my child has an object stuck in the nose or ear?",
        a: "Avoid poking at it, which can push it deeper. Seek prompt ENT care so it can be removed safely under proper vision.",
      },
      {
        q: "Is anaesthesia needed for removal?",
        a: "Many objects are removed in the clinic. Some cases, especially in anxious children, may need brief anaesthesia for safe removal.",
      },
      {
        q: "Which foreign bodies are considered an emergency?",
        a: "Button batteries, sharp objects and anything blocking the airway are emergencies and must be removed immediately to prevent serious damage.",
      },
      {
        q: "Can I try to remove it at home with tweezers or cotton buds?",
        a: "This is not advised. Home attempts often push the object deeper or injure delicate tissue. Prompt professional removal is safer and quicker.",
      },
    ],
  },
];

export function getProcedure(slug) {
  return PROCEDURES.find((p) => p.slug === slug);
}
